import {Subject} from "rxjs";
import {IGenginePlayerInfoBase} from "./i-gengine-player-info-base";
import {IMessage} from "./i-message";
import {IGenginePlayer} from "./i-gengine-player";
import {IGenginePlayerGroup, TCommandSend} from "./i-gengine-player-group";
import {IGenginePlayerInfo} from "./i-gengine-player-info";

export class GenginePlayerGroup
  implements IGenginePlayerGroup
{
  master: boolean;
  protected minUsers: number = 0;
  protected maxUsers: number = 1;
  protected userCount = 1;
  public players: IGenginePlayerInfoBase[] = [];
  public events = new Subject<string>();
  public playerEvents = new Subject<{event: string, player: IGenginePlayerInfoBase}>();
  public commands = new Subject<{command: string, value: any}>();

  constructor(
    public name: string, protected type: string,
    protected mPlayer: IGenginePlayer,
    private parent?: IGenginePlayerGroup)
  {
    this.players.push(this.makePlayerInfo(mPlayer.name));
  }

  private makePlayerInfo(name: string): IGenginePlayerInfoBase {
    if(this.parent) {
      return this.parent.player(name);
    }
    return {name}
  }

  player<TWorldData, TRoomData, TLocalData>(name: string)
    : IGenginePlayerInfo<TWorldData, TRoomData, TLocalData>| undefined
  {
    return this.players.find(p => p.name == name);
  }

  // noinspection JSUnusedGlobalSymbols
  /** @deprecated */
  get canStart() {
    return this.master && this.userCount >= this.minUsers && this.userCount <= this.maxUsers;
  }

  setMode(master: boolean): void {
    if(this.master !== master) {
      this.master = master;
      this.events.next(master?'master':'slave');
    }
  }

  setPlayers(players: string[]) {
    this.players = players.map(this.makePlayerInfo.bind(this));
  }

  // noinspection JSUnusedGlobalSymbols
  setUserLimits(min: number, max: number) {
    this.minUsers = min;
    this.maxUsers = max;
  }

  log(...args: any[]) {
    console.log(new Date().toLocaleTimeString(), ...args);
  }

  leave() {
    this.events.next('leaving');
    this.events.complete();
    this.playerEvents.complete();
    this.mPlayer.send({type: `leave ${this.type}`, data: this.name}).catch(()=>{});
    delete this.mPlayer;
  }

  onMessage(message: IMessage) {
    let index;
    try {
      switch (message.type) {
        case `joined ${this.type}`:
          if(message.data[this.type].endsWith(`/${this.name}`)) {
            index = this.players.findIndex(u => u.name === message.data.player);
            if (index < 0) {
              this.userCount++;
              const player = this.makePlayerInfo(message.data.player);
              this.players.push(player);
              this.playerEvents.next({event: 'add', player});
              if (this.userCount == this.minUsers) {
                this.events.next('enough players');
              }
            }
            this.log(message);
          }
          break;
        case `left ${this.type}`:
          if(message.data[this.type].endsWith(`/${this.name}`)) {
            index = this.players.findIndex(u => u.name === message.data.player);
            if(index >= 0) {
              this.userCount--;
              const player = this.players.splice(index, 1)[0];
              this.playerEvents.next({event: 'del', player});
              if (this.userCount < this.minUsers) {
                this.events.next('not enough players');
              }
            } else {
              this.log(`didn't have player ${message.data.player}`);
            }
            this.log(message);
          }
          break;
        case `${this.type} players`:
          if(message.data[this.type].endsWith(`/${this.name}`)) {
            this.players = message.data.players.map(this.makePlayerInfo.bind(this));
          }
          break;
        case `command ${this.type}`:
          if(message.data[this.type] == this.name) {
            this.commands.next(message.data);
          }
          break;
      }
    } catch(e) {
      console.error(e);
    }
  }

  async command(command: string, value: any, send:TCommandSend = 'others', player?: string): Promise<void> {
    await this.mPlayer.send({type: `command ${this.type}`, data: {
        command, name: this.name, send, player, value }});
  }

}
